import get from 'lodash/get'
import map from 'lodash/map'
import { TYPES } from './types'

/**
 * Translate a `NotificationFeedItem` into the row shape the bell already renders (EL-2463).
 *
 * ## Why an adapter rather than new renderers
 *
 * Both notification surfaces switch over ~20 notification types and have drifted apart once
 * already. Changing the fetch layer and the renderers in one commit makes a regression
 * impossible to bisect, so this maps the GraphQL shape onto the keys the existing switches read
 * and the renderers are left alone. The reducer `fromJS()`-es the result, so these are PLAIN
 * objects — returning Immutable here would double-wrap.
 *
 * ## The names do not line up, and that is the job
 *
 * The gateway speaks `contentId` / `contentSeries` / `contentAssetReferences`; the REST node
 * spoke `nid` / `series` / `keyart_16x9_withtext`. Every one of those translations lives here,
 * in one place, rather than in two render switches.
 *
 * @module services/notifications/feedAdapter
 */

/** Target `__typename`s, named once so a branch cannot drift from the schema. */
export const TARGET_TYPES = Object.freeze({
  CONTENT: 'NotificationContentTarget',
  COMMENT: 'NotificationCommentTarget',
  CIRCLE: 'NotificationCircleTarget',
  MEMBER: 'NotificationMemberTarget',
})

/**
 * The 16x9 thumbnail for a hydrated content row.
 *
 * Only 16x9 keyart slots — with-text first, then no-text — matching the legacy `/node` JSON
 * `keyart_16x9_withtext` bag. Other catalog art (2x3 poster, hero, etc.) must not appear in
 * notification tiles.
 *
 * Returns `''` when no 16x9 keyart exists; renderers guard on that rather than emitting
 * `<img src="">`.
 */
const THUMBNAIL_PREFERENCE = ['d6-keyart-with-text', 'd6-keyart-no-text']

function pickFromTypedRows (rows, typeKey, urlKey, languageKey) {
  const list = rows || []
  if (!list.length) {
    return ''
  }
  const byType = (type) => list.filter((row) => get(row, typeKey) === type)
  const candidates = THUMBNAIL_PREFERENCE.reduce(
    (found, type) => (found.length ? found : byType(type)),
    [],
  )
  if (!candidates.length) {
    return ''
  }
  const preferred = candidates.find((row) => get(row, languageKey) === 'en') || candidates[0]
  return get(preferred, urlKey, '') || ''
}

function pickImage (content) {
  const fromAssets = pickFromTypedRows(
    get(content, 'contentAssetReferences') || [],
    'contentAssetReferenceType.type',
    'asset.renderUrl',
    'asset.language',
  )
  if (fromAssets) {
    return fromAssets
  }
  // Admin Images (and publishingImageList) use ImageContentRelationships; many published
  // titles still have d6 keyart there while content_asset_references is incomplete.
  return pickFromTypedRows(
    get(content, 'imageContentRelationships') || [],
    'type',
    'image.url',
    'language',
  )
}

/**
 * The hydrated content, under the keys the renderers read as `node`.
 *
 * ⚠️ `keyart_16x9_withtext.keyart_304x171` is reproduced as a SHAPE, not as a real pre-sized
 * asset — the renderers index into it and changing them is out of scope for this commit. When
 * the renderers are modernised this nesting should go with them.
 */
/**
 * A comment notification's destination, rebuilt onto a path web can actually resolve.
 *
 * ⚠️ Brooklyn's `links[].path` is `/video/<contentId>?comment=<id>` — the portable
 * cross-platform contract, which native routes by id. **Web resolves SLUGS.** Verified against
 * stage: `pathInfo(path: "video/2705")` answers `success: false, type: "unknown"`, so that link
 * lands on a detail page that never receives content and sits on comment skeletons forever —
 * with or without the `?comment=` param, which is why this looked like a deep-link bug and was
 * not one (EL-2463).
 *
 * The slug is already in hand on the TYPED target, which is exactly where the content rows read
 * theirs from (`renderRecommendation` takes `node.path`, ignoring `links` — Brooklyn's
 * free-form link for "Outsourced" is the string `charles/test`). Built from the target rather
 * than parsed out of the link string, for the reason `circleLink` gives: a client that
 * reverse-engineers a deeplink is a client that breaks when the link format changes.
 *
 * Returns null — leaving Brooklyn's link untouched — when there is no slug to use.
 */
function commentDeepLinkPath (target) {
  if (get(target, '__typename') !== TARGET_TYPES.COMMENT) return null
  const path = get(target, 'content.path')
  const commentId = get(target, 'commentId')
  if (!path || commentId == null) return null
  // The feed answers a bare `video/<slug>`; a link needs it root-relative, or the browser
  // resolves it against whatever directory the member is currently in.
  const rooted = String(path).startsWith('/') ? String(path) : `/${path}`
  return `${rooted}${rooted.includes('?') ? '&' : '?'}comment=${commentId}`
}

function toNode (content) {
  if (!content) {
    return undefined
  }
  // `contentSeries` is a list; the renderers only ever showed one series line.
  const series = get(content, 'contentSeries.0')
  return {
    nid: get(content, 'contentId'),
    title: get(content, 'title'),
    path: get(content, 'path'),
    type: get(content, 'contentType'),
    language: get(content, 'language'),
    /**
     * ⚠️ Coerced to a NUMBER, because REST's `node.feature.nid` was one.
     *
     * `featureMediaId` is `ID` on the gateway, so it arrives as `"10956"`. Three readers take
     * it: GA4's `media_id` on both surfaces, and `NotificationsVideoPromotionModal`. Letting a
     * string through would quietly change the type of a live analytics parameter — nothing
     * would break, the dashboards would just stop agreeing with themselves.
     */
    feature: { nid: Number(get(content, 'featureMediaId')) || null },
    series: series
      ? {
        // GA4 reads `series.nid` as `parent_series_id`.
        nid: get(series, 'contentId'),
        title: get(series, 'title'),
        path: get(series, 'path'),
      }
      : undefined,
    keyart_16x9_withtext: { keyart_304x171: pickImage(content) },
  }
}

/**
 * The `member` block: avatar always, name and profile link only where the row is ABOUT a member.
 *
 * ⚠️ The asymmetry is deliberate and predates this adapter. A comment reply and a circle reaction
 * both carry an actor, but their destination is the video or the circle drawer — filling
 * `member.url` with the actor's profile would quietly send someone who tapped "replied to your
 * post" to a profile page instead. Only `PROFILE_FOLLOWED` is genuinely about the person.
 */
function toMember (node) {
  const target = get(node, 'target')
  const isMemberTarget = get(target, '__typename') === TARGET_TYPES.MEMBER
  const profile = isMemberTarget ? get(target, 'profile') : get(node, 'actor')
  if (!profile) {
    return undefined
  }
  const image = get(profile, 'avatar.url', '')
  const aboutThisMember = isMemberTarget
    || get(node, 'type') === TYPES.PROFILE_FOLLOWED
  if (!aboutThisMember) {
    return { image }
  }
  const uuid = get(profile, 'uuid')
  return {
    image,
    title: get(profile, 'displayName', '') || get(node, 'name', ''),
    url: uuid ? `/profile/${uuid}` : '',
  }
}

/**
 * The target's flat keys, which the renderers and the link builders still read.
 *
 * `contentTitle` is the CIRCLE's name. The row's own `name` holds the ACTOR on the reply and
 * mention types, which is why the server exposes the circle separately and why composing the
 * sentence client-side needs both (CO-751).
 */
function toTargetFields (target) {
  switch (get(target, '__typename')) {
    case TARGET_TYPES.CONTENT:
      return {
        contentId: get(target, 'contentId'),
        contentType: get(target, 'contentType'),
        node: toNode(get(target, 'content')),
      }
    case TARGET_TYPES.COMMENT:
      return {
        commentId: get(target, 'commentId'),
        contentId: get(target, 'contentId'),
        node: toNode(get(target, 'content')),
      }
    case TARGET_TYPES.CIRCLE:
      return {
        circleUuid: get(target, 'circleUuid'),
        postUuid: get(target, 'postUuid'),
        contentTitle: get(target, 'name', ''),
      }
    case TARGET_TYPES.MEMBER:
      // `NotificationMemberTarget.uid` is `Int`, but the legacy portal follow compares it
      // against `contentId`, so it is normalised here rather than at each reader.
      return { contentId: Number(get(target, 'uid')) || null }
    // `null` target — moderation notices and rejected comments have nothing to open.
    default:
      return {}
  }
}

/**
 * One notification, in the shape the renderers already consume.
 *
 * ⚠️ `timestamp` and `timeAsDistance` are NOT reproduced. REST rendered those server-side as
 * localised strings; the feed returns a raw `startDate` because a client formats a date better
 * than the server can guess for a cached response. Both surfaces already format `startDate`.
 */
export function fromFeedItem (node) {
  if (!node) {
    return null
  }
  const commentPath = commentDeepLinkPath(get(node, 'target'))
  return {
    /**
     * ⚠️ Coerced to STRING, and every id this app compares must agree.
     *
     * `NotificationFeedItem.id` is `ID`, so it arrives a string where REST sent a number.
     * `SET_RECEIVED` matches with `Set.has(r.get('id'))` and `markTabRead` keeps a `markedIds`
     * set — a string id against a numeric one matches NOTHING, so mark-as-read would appear to
     * work and silently never clear the badge.
     */
    id: String(get(node, 'id', '')),
    type: get(node, 'type'),
    /**
     * Server-authoritative. The client type→tab map is gone; this is the one source.
     *
     * ⚠️ LOWERCASED, and that is not cosmetic. The GraphQL enum is `COMMUNITY` / `CONTENT`
     * while `notificationCategory.CATEGORIES` — the tab keys, the `?tab=` URL param and the
     * Redux slice paths — are lowercase. Passing the enum through verbatim makes every
     * `item.category === tab` comparison false, which silently empties both tabs rather than
     * failing loudly. Verified against stage, where the feed really does answer `"COMMUNITY"`.
     */
    category: String(get(node, 'category', '')).toLowerCase(),
    name: get(node, 'name', ''),
    label: get(node, 'label', ''),
    reason: get(node, 'reason', ''),
    message: get(node, 'message', ''),
    received: get(node, 'received', false),
    startDate: get(node, 'startDate'),
    // The bell hides an expired video promotion on this.
    endDate: get(node, 'endDate'),
    actorCount: get(node, 'actorCount'),
    links: map(get(node, 'links') || [], (link) => ({
      id: get(link, 'id'),
      type: get(link, 'type'),
      path: get(link, 'type') === 'WEB' && commentPath
        ? commentPath
        : get(link, 'path'),
      external: get(link, 'external', false),
    })),
    member: toMember(node),
    ...toTargetFields(get(node, 'target')),
  }
}

/**
 * A whole page: the rows plus the paging and count state the reducer stores.
 *
 * ⚠️ `unseenCount` is the badge, and it is a total across BOTH tabs — it is not narrowed by the
 * `category` that produced this page, by design on both the server and the client (CO-768). It
 * already excludes `GENERAL_VIDEO_PROMOTION`, which the bell never draws.
 */
export function fromFeedConnection (connection) {
  const edges = get(connection, 'edges') || []
  return {
    rows: edges.map((edge) => fromFeedItem(get(edge, 'node'))).filter(Boolean),
    total: get(connection, 'totalCount', 0),
    unseenCount: get(connection, 'unseenCount', 0),
    hasNextPage: get(connection, 'pageInfo.hasNextPage', false),
    endCursor: get(connection, 'pageInfo.endCursor', null),
  }
}

export default fromFeedItem
